import { z } from 'zod';

// Asia/Almaty is UTC+5 all year (single Kazakhstan time zone since 2024-03-01).
const ALMATY_OFFSET_MS = 5 * 60 * 60 * 1000;

const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/** Today's date in Almaty as YYYY-MM-DD. */
export function almatyToday(now: Date = new Date()): string {
  return new Date(now.getTime() + ALMATY_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * Last day a lot is still valid, YYYY-MM-DD in Almaty time.
 * `expiresAt` is 00:00 Almaty of the following day, so step back 1 ms.
 */
export function lastValidDay(expiresAt: string | Date): string {
  const t = new Date(expiresAt).getTime() + ALMATY_OFFSET_MS - 1;
  return new Date(t).toISOString().slice(0, 10);
}

/** «11 октября» — the last valid day. */
export function formatValidUntil(expiresAt: string | Date): string {
  const [, month, day] = lastValidDay(expiresAt).split('-').map(Number);
  return `${day} ${MONTHS_GENITIVE[month! - 1]}`;
}

/** «11.10» — the last valid day, for tight spots like the home header chip. */
export function formatValidUntilShort(expiresAt: string | Date): string {
  const [, month, day] = lastValidDay(expiresAt).split('-');
  return `${day}.${month}`;
}

/** 1 кредит, 3 кредита, 5 кредитов. */
export function pluralCredits(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'кредит';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'кредита';
  return 'кредитов';
}

export const creditLotSourceSchema = z.enum([
  'bonus_invite',
  'bonus_monthly',
  'manual',
  'refund',
  'purchase',
  'bonus_welcome',
]);
export type CreditLotSource = z.infer<typeof creditLotSourceSchema>;

export const creditLotDtoSchema = z.object({
  source: creditLotSourceSchema,
  amount: z.number().int().positive(),
  remaining: z.number().int().nonnegative(),
  expiresAt: z.string(),
});
export type CreditLotDto = z.infer<typeof creditLotDtoSchema>;

/** GET /credits */
export const creditsResponseSchema = z.object({
  lots: z.array(creditLotDtoSchema),
});
export type CreditsResponse = z.infer<typeof creditsResponseSchema>;

/** GET /pricing — prices in credits, from the settings table. */
export const pricingSchema = z.object({
  thought: z.number().int().nonnegative(),
  carouselStatic: z.number().int().nonnegative(),
  carouselAnimated: z.number().int().nonnegative(),
  reel: z.number().int().nonnegative(),
  /** A «Схема» reel. */
  reelDiagram: z.number().int().nonnegative(),
  reelListicle: z.number().int().nonnegative(),
  /** Surcharge of a reel voiced by the clone. */
  voiceClone: z.number().int().nonnegative(),
  freeRebuilds: z.number().int().nonnegative(),
});
export type Pricing = z.infer<typeof pricingSchema>;
